import React from 'react';
import { alpha, Box, Typography } from '@mui/material';

/** Visual style for the panel and icon background. */
export type StatePanelTone = 'primary' | 'error';

interface StatePanelProps {
    /** Icon displayed above the title. */
    icon: React.ReactNode;
    tone: StatePanelTone;
    /** Main panel heading. */
    title: string;
    /** Optional supporting text. */
    description?: React.ReactNode;
    /** Optional action, such as a retry button. */
    children?: React.ReactNode;
}

/**
 * Shared layout for empty and error states.
 */
const StatePanel: React.FC<StatePanelProps> = ({ icon, tone, title, description, children }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 1.5,
            px: 3,
            py: 7,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: '14px',
            bgcolor: (theme) => alpha(theme.palette[tone].main, 0.03),
        }}
    >
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: (theme) => alpha(theme.palette[tone].main, 0.1),
            }}
        >
            {icon}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {title}
        </Typography>
        {/* Rendered as a div, not the default <p>, because callers pass elements such as a
            Box-wrapped button in the description. A div inside a p is invalid HTML and React
            warns about it. Skipped entirely when there is no description, so panels without
            one do not carry an empty element and its gap. */}
        {description ? (
            <Typography variant="body2" component="div" color="text.secondary" sx={{ maxWidth: 420 }}>
                {description}
            </Typography>
        ) : null}
        {children}
    </Box>
);

export default StatePanel;
